import { ShiftsPlanningVersionFragment } from 'generated/resources/graphql'
import first from 'lodash/first'
import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import { computed, makeObservable } from 'mobx'
import type { Moment } from 'moment'

import PlanningVersion from 'modules/shift_management/store/internal/models/PlanningVersion'

class PlanningVersions {
  planningVersions: PlanningVersion[]

  constructor(planningVersions: PlanningVersion[]) {
    this.planningVersions = planningVersions

    makeObservable(this, {
      _groupedByEmployeeId: computed,
    })
  }

  static buildFromGraphQL(planningVersions: ShiftsPlanningVersionFragment[]): PlanningVersions {
    // FIXME: revisit the plannigVersion type
    const planningVersionsObjects = planningVersions.map((pv) =>
      PlanningVersion.buildFromGraphQL(pv)
    )

    return new PlanningVersions([PlanningVersion.default(0)].concat(planningVersionsObjects))
  }

  get _groupedByEmployeeId(): Record<number, PlanningVersion[]> {
    return groupBy(this.planningVersions, (pv) => pv.employeeId)
  }

  planningVersionsByEmployeeId(employeeId: number): PlanningVersion[] {
    const planningVersions = this._groupedByEmployeeId[employeeId] || []
    return [PlanningVersion.default(employeeId)].concat(planningVersions)
  }

  isAfterThanNextPlanningVersion(models: PlanningVersion[], index: number, date: Moment) {
    return (
      // last one
      models[index + 1]?.effectiveAt.isAfter(date, 'day') || index === models.length - 1
    )
  }

  isBeforeThanDate(model: PlanningVersion, date: Moment) {
    return model.effectiveAt.isSameOrBefore(date, 'day')
  }

  currentPlanningVersionWithinDate(models: PlanningVersion[], date: Moment) {
    const currentPlanningVersion = models.find(
      (model: PlanningVersion, index: number) =>
        this.isBeforeThanDate(model, date) &&
        this.isAfterThanNextPlanningVersion(models, index, date)
    )

    return currentPlanningVersion || first(models)
  }

  planningVersionWithinDate(employeeId: number, date: Moment): PlanningVersion | undefined {
    return this.currentPlanningVersionWithinDate(
      orderBy(
        this.planningVersionsByEmployeeId(employeeId),
        (pv: PlanningVersion) => pv.effectiveAt,
        'asc'
      ),
      date
    )
  }

  filterByPlanningTool(planningTool: string): PlanningVersion[] {
    return this.planningVersions.filter((pv: PlanningVersion) => pv.planningTool === planningTool)
  }
}

export default PlanningVersions
